import React, { useState } from 'react'
import { X } from 'lucide-react'
import toast from 'react-hot-toast'
import { Team } from '../../types/teams'
import { teamsService } from '../../services/teamsService'
import { imageUploadService } from '../../services/imageUploadService'
import { FILL_GRADIENT, LINE_GRADIENT } from '../../constants/gradients'

interface TeamEditModalProps {
  isOpen: boolean
  onClose: () => void
  team: Team
  onSave: () => void
}

export const TeamEditModal: React.FC<TeamEditModalProps> = ({
  isOpen,
  onClose,
  team,
  onSave
}) => {
  const [name, setName] = useState(team.name || '')
  const [slug, setSlug] = useState((team as any).slug || '')
  const [description, setDescription] = useState(team.description || '')
  const [avatarUrl, setAvatarUrl] = useState(team.avatar_url || '')
  const [isLoading, setIsLoading] = useState(false)
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)

  const handleAvatarUpload = async (file: File) => {
    let preview: string | null = null
    
    try {
      setIsUploadingAvatar(true)
      
      // Generate and show preview while uploading
      preview = imageUploadService.generatePreviewUrl(file)
      setAvatarUrl(preview)
      
      // Upload with moderation
      const result = await imageUploadService.uploadImage(
        file,
        team.id,
        'avatar'
      )
      
      // Clean up preview and show final URL
      imageUploadService.cleanupPreviewUrl(preview)
      setAvatarUrl(result.url)
      toast.success('Avatar uploaded successfully')
    } catch (error) {
      console.error('Failed to upload avatar:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload avatar'
      
      // REMOVE preview if moderation failed
      if (preview) {
        imageUploadService.cleanupPreviewUrl(preview)
        setAvatarUrl(team.avatar_url || '') // Revert to original
      }
      
      // Show specific error message (includes moderation failures)
      toast.error(errorMessage)
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleAvatarUpload(file)
    }
  }

  const handleRemoveAvatar = () => {
    setAvatarUrl('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (!name.trim()) {
      toast.error('Team name is required')
      return
    }

    if (name.length > 100) {
      toast.error('Team name must be 100 characters or less')
      return
    }

    if (description.length > 1000) {
      toast.error('Description must be 1000 characters or less')
      return
    }

    try {
      setIsLoading(true)
      
      await teamsService.updateTeam(team.id, {
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim(),
        avatar_url: avatarUrl
      })

      toast.success('Team updated successfully')
      onSave()
      onClose()
    } catch (error: any) {
      console.error('Failed to update team:', error)
      toast.error(error.response?.data?.message || 'Failed to update team')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
          {/* Gradient line at top */}
          <div className="h-1" style={{ background: LINE_GRADIENT }} />
          
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200 dark:border-dark-border">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-dark-text">Edit Team</h2>
              <p className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">Update your team information</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              disabled={isLoading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Avatar Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-3">
                Team Avatar
              </label>
              <div className="flex items-center gap-4">
                {/* Avatar Preview */}
                <div 
                  className="w-24 h-24 rounded-full flex items-center justify-center overflow-hidden border-2 border-gray-200 dark:border-gray-700"
                  style={{ background: avatarUrl ? 'transparent' : FILL_GRADIENT }}
                >
                  {avatarUrl ? (
                    <img 
                      src={avatarUrl} 
                      alt="Team avatar" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl font-bold text-white">
                      {name.charAt(0).toUpperCase() || 'T'}
                    </span>
                  )}
                </div>

                {/* Upload Controls */}
                <div className="flex-1">
                  <div className="flex gap-3">
                    <label className="px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 cursor-pointer border border-gray-200 dark:border-gray-700">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        disabled={isUploadingAvatar || isLoading}
                        className="hidden"
                      />
                      {isUploadingAvatar ? 'Uploading...' : 'Upload Avatar'}
                    </label>
                    {avatarUrl && (
                      <button
                        type="button"
                        onClick={handleRemoveAvatar}
                        disabled={isUploadingAvatar || isLoading}
                        className="px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-2">
                    Recommended: Square image, at least 200x200px
                  </p>
                </div>
              </div>
            </div>

            {/* Team Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                Team Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                disabled={isLoading}
                className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-dark-border rounded-xl transition-colors focus:outline-none focus:border-gray-300 dark:focus:border-gray-600 dark:bg-dark-bg dark:text-dark-text font-medium disabled:opacity-50"
                placeholder="Enter team name"
              />
              <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-2">
                {name.length}/100 characters
              </p>
            </div>

            {/* Team Slug */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                Team Slug
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, ''))}
                maxLength={50}
                disabled={isLoading}
                className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-dark-border rounded-xl transition-colors focus:outline-none focus:border-gray-300 dark:focus:border-gray-600 dark:bg-dark-bg dark:text-dark-text font-medium disabled:opacity-50"
                placeholder="team-slug"
              />
              <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-2">
                Lowercase letters, numbers, hyphens, and underscores only
              </p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-dark-text mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={1000}
                rows={4}
                disabled={isLoading}
                className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-dark-border rounded-xl transition-colors focus:outline-none focus:border-gray-300 dark:focus:border-gray-600 dark:bg-dark-bg dark:text-dark-text font-medium resize-none disabled:opacity-50"
                placeholder="Enter team description"
              />
              <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-2">
                {description.length}/1000 characters
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-6 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || isUploadingAvatar}
                className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2"
                style={{ background: FILL_GRADIENT }}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default TeamEditModal


